import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid } from '@mui/material';
import { RowSection } from '../Common/RowSection';
import { makeStyles } from 'tss-react/mui';
import {
  selectClinicianToPatientRatio,
  selectPage2,
  selectPage2OrderPriorities,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { useSelector } from 'react-redux';
import { VirtualInterviewIdList } from '@AMIEWEB/NewOrder/constants';
import { IAutoCompleteOption } from '../Common/AutoCompleteRenderChips';
import { missingField } from 'app/constants';

const useStyles = makeStyles()(theme => ({
  boxInlineHeadLabelTxt: {
    'font-weight': '600',
    'margin-top': '0px',
    fontSize: '16px',
  },
  list: {
    paddingLeft: theme.spacing(3),
    marginTop: 0,
    marginBottom: 0,
    font: 'normal normal normal 14px/19px Roboto',
  },
}));

interface AdditionalRequirementsTwoProps {
  isNewOrderCreation: boolean;
}

class AdditionalRequirementsTwoFormData {
  clinicianToPatientRatio: string | null = missingField;

  notifyCandidate: string = missingField;

  productivityPercentage: string | null = missingField;

  patientsSeen: number | null | string;

  numberOfVisits: number | null | string;

  orderPrioritiesData: IAutoCompleteOption[] = [];

  virtualRating: IAutoCompleteOption[] = [];

  virtualRatingSubmissionRequirement: string = missingField;

  workHistoryExperience: string = missingField;
}

const AdditionalRequirementsTwo = (props: AdditionalRequirementsTwoProps) => {
  const { isNewOrderCreation } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();
  const clinicianToPatientRatioData = useSelector(selectClinicianToPatientRatio);
  const selectOrderAdditionalRequirement = useSelector(selectPage2);
  const orderPrioritiesData = useSelector(selectPage2OrderPriorities);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);

  const displayData = new AdditionalRequirementsTwoFormData();
  const virtualRating =
    orderPrioritiesData?.filter(orderPriority => {
      return VirtualInterviewIdList.includes(orderPriority.value);
    }) ?? [];
  if (isNewOrderCreation) {
    displayData.clinicianToPatientRatio = `${
      isNaN(clinicianToPatientRatioData?.numberOfClinicians) || !clinicianToPatientRatioData?.numberOfClinicians
        ? missingField
        : clinicianToPatientRatioData?.numberOfClinicians
    } : ${
      !clinicianToPatientRatioData?.numberOfPatients
        ? missingField
        : clinicianToPatientRatioData?.numberOfPatients
    }`;
    displayData.notifyCandidate = clinicianToPatientRatioData?.notifyCandidate ? 'Yes' : 'No';
    displayData.productivityPercentage = clinicianToPatientRatioData?.productivityPercentage
      ? `${clinicianToPatientRatioData?.productivityPercentage}% `
      : missingField;
    displayData.patientsSeen = clinicianToPatientRatioData?.patientsSeen ?? missingField;
    displayData.numberOfVisits = clinicianToPatientRatioData?.numberOfVisits ?? missingField;
    displayData.orderPrioritiesData = orderPrioritiesData;
    displayData.virtualRating = virtualRating;
    displayData.virtualRatingSubmissionRequirement =
      selectOrderAdditionalRequirement?.virtualRatingSubmissionRequirement ? 'Yes' : 'No';
    displayData.workHistoryExperience = selectOrderAdditionalRequirement?.orderWorkHistory?.entireWorkHistory
      ? `${selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInDays ?? missingField}  ${
          selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInDays === 1 ? 'day' : 'days'
        } within their ${t(
          'facility.units.unitDetails.additionalInformation.workHistoryGap.entireWorkHistoryLowercase',
        )}`
      : selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInYears > 0
      ? `${selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInDays ?? missingField}  ${
          selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInDays === 1 ? 'day' : 'days'
        } within the last ${selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInYears} 
        ${selectOrderAdditionalRequirement?.orderWorkHistory?.workHistoryGapInYears === 1 ? 'year' : 'years'}`
      : missingField;
  } else {
    displayData.clinicianToPatientRatio = orderDetailsUIView.orderDetails.orderClinicianToPatientRatio
      ? `${orderDetailsUIView.orderDetails.orderClinicianToPatientRatio.numberOfClinicians ?? missingField} : ${
          orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio?.rangeOfPatients ?? missingField
        }`
      : missingField;
    displayData.notifyCandidate =
      orderDetailsUIView?.orderDetails?.orderClinicianToPatientRatio &&
      orderDetailsUIView.orderDetails.orderClinicianToPatientRatio.notifyCandidate
        ? 'Yes'
        : 'No';
    displayData.productivityPercentage =
      orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio &&
      orderDetailsUIView.orderDetails.orderClinicianToPatientRatio.productivityPercentage
        ? `${orderDetailsUIView.orderDetails.orderClinicianToPatientRatio.productivityPercentage}%`
        : missingField;
    displayData.patientsSeen =
      orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio &&
      orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio?.rangeOfPatientsSeen
        ? orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio?.rangeOfPatientsSeen
        : missingField;
    displayData.numberOfVisits =
      orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio &&
      orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio?.rangeOfVisits
        ? orderDetailsUIView.orderDetails?.orderClinicianToPatientRatio?.rangeOfVisits
        : missingField;
    displayData.orderPrioritiesData = orderDetailsUIView.orderDetails.orderPriorities
      ? orderDetailsUIView.orderDetails.orderPriorities.map<IAutoCompleteOption>(x => {
          return {
            name: x.description,
            value: `${x.orderPriorityTypeId}`,
            tooltip: x.description,
            hideDeleteIcon: false,
          };
        })
      : [];
    displayData.virtualRating =
      displayData.orderPrioritiesData?.filter(orderPriority => {
        return VirtualInterviewIdList.includes(orderPriority.value);
      }) ?? [];
    displayData.virtualRatingSubmissionRequirement = orderDetailsUIView.orderDetails.virtualRatingSubmissionRequirement
      ? 'Yes'
      : 'No';
    displayData.workHistoryExperience = orderDetailsUIView?.orderDetails?.orderWorkHistory?.entireWorkHistory
      ? `${orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInDays ?? missingField} ${
          orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInDays === 1 ? 'day' : 'days'
        } within their ${t(
          'facility.units.unitDetails.additionalInformation.workHistoryGap.entireWorkHistoryLowercase',
        )}`
      : orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInYears > 0
      ? `${orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInDays ?? missingField}  ${
          orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInDays === 1 ? 'day' : 'days'
        }  within the last ${orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInYears}  ${
          orderDetailsUIView?.orderDetails?.orderWorkHistory?.workHistoryGapInYears === 1 ? 'year' : 'years'
        }`
      : missingField;
  }

  return (
    <Grid sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Grid item direction="column" width="100%">
        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>
              {t('order.newOrderCreation.tabs.clinicianToPatientRatio')}
            </div>
          }
          data={``}
        />

        <RowSection
          header={t('order.newOrderCreation.tabs.clinicianToPatientRatio')}
          data={displayData.clinicianToPatientRatio}
        />
        <RowSection header={t('order.newOrderCreation.tabs.notifyCandidate')} data={displayData.notifyCandidate} />
        <RowSection
          header={t('order.newOrderCreation.tabs.productivityPercentage')}
          data={displayData.productivityPercentage}
        />
        <RowSection header={t('order.newOrderCreation.tabs.patientsSeen')} data={displayData.patientsSeen} />
        <RowSection header={t('order.newOrderCreation.tabs.numberOfVisits')} data={displayData.numberOfVisits} />
        <Divider style={{ marginBottom: '12px' }} />
        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>
              {t('order.newOrderCreation.tabs.virtualInterviewRating')}
            </div>
          }
          data={``}
        />

        <Box marginBottom={'6px'}>
          {displayData.virtualRating?.map((data, index) => (
            <ul key={index} className={classes.list}>
              <li key={index}> {data.name} </li>
            </ul>
          ))}
        </Box>
        <RowSection
          header={t('facility.units.unitDetails.additionalInformation.submissionRequirement')}
          data={displayData.virtualRatingSubmissionRequirement}
        />

        <Divider style={{ marginBottom: '12px' }} />

        <RowSection
          header={<div className={classes.boxInlineHeadLabelTxt}>{t('order.newOrderCreation.tabs.workHistory')}</div>}
          data={``}
        />
        <Box sx={{ minWidth: '100%', maxWidth: '0px' }}>
          {displayData.workHistoryExperience !== missingField ? (
            <RowSection
              header={t('facility.units.unitDetails.additionalInformation.workHistoryGap.noGapsGreaterThan')}
              data={displayData.workHistoryExperience}
            />
          ) : (
            missingField
          )}
        </Box>
      </Grid>
    </Grid>
  );
};

export default AdditionalRequirementsTwo;
